import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const QUICK_ADDON_PROPERTY = '_zs_quick_addon';

/**
 * Prevents cart item removal clicks from bubbling to the <dialog> backdrop
 * handler.
 *
 * Shopify handles the actual cart remove action during the capture phase.
 * We stop the same click afterwards, inside the cart drawer, so it can never
 * be mistaken for a backdrop click when the clicked row is removed from DOM.
 */
function initCartDrawerRemoveGuard() {
  const drawer = document.querySelector('#cart-drawer');

  if (!(drawer instanceof HTMLElement)) return;

  const bindGuard = () => {
    const component = drawer.querySelector('cart-drawer-component');

    if (!(component instanceof HTMLElement)) return;
    if (component.dataset.zsRemoveGuard === 'true') return;

    component.dataset.zsRemoveGuard = 'true';

    component.addEventListener('click', (event) => {
      const path =
        typeof event.composedPath === 'function'
          ? event.composedPath()
          : [];

      const clickedRemoveButton = path.some(
        (node) =>
          node instanceof Element &&
          node.classList.contains('cart-items__remove')
      );

      if (!clickedRemoveButton) return;

      /*
       * Important:
       * do NOT preventDefault().
       *
       * The Shopify cart action has already been triggered.
       * We only prevent this click from continuing to the dialog backdrop.
       */
      event.stopPropagation();
    });
  };

  bindGuard();

  /*
   * Cart content is re-rendered after cart mutations.
   * Re-bind automatically if Shopify ever replaces the component.
   */
  if (drawer.dataset.zsRemoveGuardObserver === 'true') return;

  drawer.dataset.zsRemoveGuardObserver = 'true';

  const observer = new MutationObserver(bindGuard);

  observer.observe(drawer, {
    childList: true,
    subtree: true,
  });
}

class ZsCartQuickAddon extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-zs-quick-addon-toggle]');
    this.status = this.querySelector('[data-zs-quick-addon-status]');

    if (!this.toggle || this.dataset.bound === 'true') return;

    this.dataset.bound = 'true';
    this.toggle.addEventListener('click', this.handleToggle);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.handleToggle);
  }

  handleToggle = async () => {
    if (!this.toggle || this.toggle.disabled) return;

    const isInCart = this.dataset.inCart === 'true';

    if (isInCart) {
      await this.removeFromCart();
    } else {
      await this.addToCart();
    }
  };

  getSectionIds() {
    const sectionIds = new Set();

    document
      .querySelectorAll('cart-items-component')
      .forEach((component) => {
        if (
          component instanceof HTMLElement &&
          component.dataset.sectionId
        ) {
          sectionIds.add(component.dataset.sectionId);
        }
      });

    return Array.from(sectionIds);
  }

  async fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  setLoading(loading, nextChecked, statusText) {
    if (!this.toggle) return;

    this.toggle.disabled = loading;

    this.toggle.setAttribute(
      'aria-checked',
      nextChecked ? 'true' : 'false'
    );

    this.setAttribute(
      'aria-busy',
      loading ? 'true' : 'false'
    );

    if (this.status && statusText) {
      this.status.textContent = statusText;
    }
  }

  restoreState() {
    const isInCart = this.dataset.inCart === 'true';

    this.setLoading(
      false,
      isInCart,
      isInCart
        ? this.dataset.addedLabel
        : this.dataset.addLabel
    );
  }

  dispatchCartError(error) {
    this.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Cart update failed',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  }

  async addToCart() {
    const variantId = Number(this.dataset.variantId);

    if (!variantId) return;

    this.setLoading(
      true,
      true,
      this.dataset.addingLabel
    );

    const sectionIds = this.getSectionIds();
    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [
          {
            merchandiseId: variantId.toString(),
            quantity: 1,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  [QUICK_ADDON_PROPERTY]: 'true',
                },
              },
            ],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        }
      );

      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        throw new Error(
          responseData.description ||
            responseData.message ||
            'Unable to add this item to the cart.'
        );
      }

      const cart = await this.fetchCart();

      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            cart
          ),
        detail: {
          items: cart.items,
          source: 'zs-cart-quick-addon',
          itemCount: 1,
          sections: responseData.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(
        '[ZS quick add-on] Add failed:',
        error
      );

      deferredEventPromise.reject(error);

      this.restoreState();
      this.dispatchCartError(error);
    }
  }

  async removeFromCart() {
    const lineKey = this.dataset.lineKey;

    if (!lineKey) return;

    this.setLoading(
      true,
      false,
      this.dataset.removingLabel
    );

    const sectionIds = this.getSectionIds();
    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [
          {
            id: lineKey,
            quantity: 0,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_change_url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        }
      );

      const responseData = await response.json();

      if (!response.ok || responseData.errors) {
        throw new Error(
          responseData.errors ||
            responseData.description ||
            responseData.message ||
            'Unable to remove this item from the cart.'
        );
      }

      const cart = await this.fetchCart();

      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            cart
          ),
        detail: {
          items: cart.items,
          source: 'zs-cart-quick-addon',
          itemCount: 0,
          sections: responseData.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(
        '[ZS quick add-on] Remove failed:',
        error
      );

      deferredEventPromise.reject(error);

      this.restoreState();
      this.dispatchCartError(error);
    }
  }
}

if (!customElements.get('zs-cart-quick-addon')) {
  customElements.define(
    'zs-cart-quick-addon',
    ZsCartQuickAddon
  );
}

initCartDrawerRemoveGuard();